import { yupResolver } from "@hookform/resolvers/yup";
import {
  CheckIcon,
  CloseIcon,
  EyeClosedIcon,
  EyeIcon,
  GKButton,
  GKTextField,
  Grid2,
  IconButton,
  Typography,
} from "components";
import { useChangePassword } from "hooks/useChangePassword";
import { Controller, useForm } from "react-hook-form";
import useDocumentTitle from "utils/useDocumentTitle";
import * as Yup from "yup";

const ChangePassword = () => {
  const {
    isSubmitting,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    onSubmit,
  } = useChangePassword();
  useDocumentTitle("Change Password");

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required("Password is required")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        "Password must meet the requirements.",
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const form = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(validationSchema),
  });

  form.watch();

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Grid2 container gap={2} justifyContent={"center"}>
        <Grid2
          size={12}
          position={"relative"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"20px"}
          flexDirection={"column"}
        >
          <Typography
            fontWeight={700}
            fontSize={24}
            className="textUnderlineLogin"
            color={"var(--black)"}
            sx={{
              "&.textUnderlineLogin::before": {
                width: "210px !important",
              },
            }}
          >
            Change Password !
          </Typography>
          <Typography color={"var(--black)"}>
            Please fill in your details to change your password
          </Typography>
        </Grid2>
        <Grid2 container size={{ xs: 12, md: 7, sm: 8 }} gap={2}>
          <Grid2 size={12}>
            <Controller
              name="password"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="off"
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    input: {
                      style: { paddingRight: 12 },
                      endAdornment: (
                        <IconButton
                          style={{ padding: 0 }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {!showPassword ? <EyeIcon /> : <EyeClosedIcon />}
                        </IconButton>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2
            container
            size={12}
            mt={1}
            p={1}
            style={{
              border: "solid 1px",
              borderRadius: "10px",
              background: "var(--orange-400)",
              borderColor: "var(--orange-700)",
            }}
          >
            <Grid2 size={12}>
              <Typography
                whiteSpace={"nowrap"}
                fontSize={12}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  textDecoration: /[A-Z]/.test(form.getValues("password"))
                    ? "line-through"
                    : "",
                }}
              >
                {/[A-Z]/.test(form.getValues("password")) ? (
                  <CheckIcon
                    width={10}
                    height={10}
                    color={"var(--success-700)"}
                  />
                ) : (
                  <CloseIcon
                    height={10}
                    width={10}
                    color={`var(--warning-700)`}
                  />
                )}
                Password should contain an Upper Case Letter
              </Typography>
              <Typography
                fontSize={12}
                whiteSpace={"nowrap"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  textDecoration: /[a-z]/.test(form.getValues("password"))
                    ? "line-through"
                    : "",
                }}
              >
                {/[a-z]/.test(form.getValues("password")) ? (
                  <CheckIcon
                    width={10}
                    height={10}
                    color={"var(--success-700)"}
                  />
                ) : (
                  <CloseIcon
                    height={10}
                    width={10}
                    color={`var(--warning-700)`}
                  />
                )}
                Password should contain a Lower Case Letter
              </Typography>
              <Typography
                fontSize={12}
                whiteSpace={"nowrap"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  textDecoration: /[@$!%*?&_]/.test(form.getValues("password"))
                    ? "line-through"
                    : "",
                }}
              >
                {/[@$!%*?&_]/.test(form.getValues("password")) ? (
                  <CheckIcon
                    width={10}
                    height={10}
                    color={"var(--success-700)"}
                  />
                ) : (
                  <CloseIcon
                    height={10}
                    width={10}
                    color={`var(--warning-700)`}
                  />
                )}
                Password should contain a Special character
              </Typography>
              <Typography
                fontSize={12}
                whiteSpace={"nowrap"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  textDecoration: /\d/.test(form.getValues("password"))
                    ? "line-through"
                    : "",
                }}
              >
                {/\d/.test(form.getValues("password")) ? (
                  <CheckIcon
                    width={10}
                    height={10}
                    color={"var(--success-700)"}
                  />
                ) : (
                  <CloseIcon
                    height={10}
                    width={10}
                    color={`var(--warning-700)`}
                  />
                )}
                Password should contain a number
              </Typography>
              <Typography
                fontSize={12}
                whiteSpace={"nowrap"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  textDecoration: /^([a-zA-Z0-9_*&^%$#@!]){8,}$/.test(
                    form.getValues("password"),
                  )
                    ? "line-through"
                    : "",
                }}
              >
                {/^([a-zA-Z0-9_*&^%$#@!]){8,}$/.test(
                  form.getValues("password"),
                ) ? (
                  <CheckIcon
                    width={10}
                    height={10}
                    color={"var(--success-700)"}
                  />
                ) : (
                  <CloseIcon
                    height={10}
                    width={10}
                    color={`var(--warning-700)`}
                  />
                )}
                Password should atleast contain minimum 8 Characters
              </Typography>
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="confirmPassword"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  inputLabel="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="off"
                  slotProps={{
                    input: {
                      style: {
                        paddingRight: 12,
                      },
                      endAdornment: (
                        <IconButton
                          style={{ padding: 0 }}
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          {!showConfirmPassword ? (
                            <EyeIcon />
                          ) : (
                            <EyeClosedIcon />
                          )}
                        </IconButton>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12} textAlign={"end"}>
            <GKButton
              loading={isSubmitting}
              size="medium"
              variant="contained"
              type="submit"
            >
              Change Password
            </GKButton>
          </Grid2>
        </Grid2>
        <Grid2 size={12} textAlign={"center"}>
          <Typography color={"var(--black)"}>
            GridKey is Private and Secure
          </Typography>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default ChangePassword;
